import { useState, useEffect, useMemo } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog'
import { Button } from '../../ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select'
import { toast } from 'sonner'
import { useQuery } from '@tanstack/react-query'
import { facturamaService } from '../../../services/facturama.service'
import { Loader2, FileText, Search, RefreshCw, XCircle } from 'lucide-react'
import { Badge } from '../../ui/badge'
import { cn } from '../../../lib/utils'
import { GlobalPaginator } from '../../ui/global-paginator'
import { CancelCfdiModal } from './CancelCfdiModal'
import { useSessionStore } from '../../../store/auth/auth.store'

interface InvoicesHistoryModalProps {
  isOpen: boolean
  onClose: () => void
}

export function InvoicesHistoryModal({ isOpen, onClose }: InvoicesHistoryModalProps) {
  const canCancelSale = useSessionStore((state) => state.canAction('sales.sale.cancel'))
  const [filterType, setFilterType] = useState('all') // all, I (Ingreso), E (Egreso)
  const [filterStatus, setFilterStatus] = useState('active') // active o canceled
  const [keyword, setKeyword] = useState('')
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [downloadType, setDownloadType] = useState<'pdf' | 'xml' | null>(null)

  // Estado para el modal de cancelación CFDI
  const [cancelCfdiTarget, setCancelCfdiTarget] = useState<{ cfdiId: string; label: string } | null>(null)

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    from: 0,
    to: 0,
  })

  const { data: invoices, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ['facturama-invoices', filterStatus, keyword],
    queryFn: () => facturamaService.getInvoices({ status: filterStatus, keyword: keyword || undefined }),
    enabled: isOpen,
    staleTime: 0,
  })

  // Refresca cuando abre el modal
  useEffect(() => {
    if (isOpen) {
      refetch()
      setPagination(prev => ({ ...prev, page: 1 }))
    }
  }, [isOpen, refetch])

  const filteredInvoices = useMemo(() => {
    if (!invoices) return []
    if (filterType === 'all') return invoices
    return invoices.filter((inv: any) => {
      const type = inv.CfdiType || inv.TypeDocument
      return (type || 'I') === filterType
    })
  }, [invoices, filterType])

  const totalInvoices = filteredInvoices.length
  const from = totalInvoices > 0 ? (pagination.page - 1) * pagination.pageSize + 1 : 0
  const to = Math.min(pagination.page * pagination.pageSize, totalInvoices)

  const paginatedInvoices = filteredInvoices.slice(
    (pagination.page - 1) * pagination.pageSize,
    pagination.page * pagination.pageSize
  )

  const handleDownload = async (cfdiId: string, type: 'pdf' | 'xml') => {
    setDownloadingId(cfdiId)
    setDownloadType(type)
    try {
      toast.info(`Descargando ${type.toUpperCase()}...`)
      let base64 = ''
      if (type === 'pdf') {
        base64 = await facturamaService.downloadInvoicePdf(cfdiId)
      } else {
        base64 = await facturamaService.downloadInvoiceXml(cfdiId)
      }

      const link = document.createElement('a')
      const mime = type === 'pdf' ? 'application/pdf' : 'application/xml'
      link.href = `data:${mime};base64,${base64}`
      link.download = `Factura_${cfdiId}.${type}`
      link.click()

      toast.success(`${type.toUpperCase()} descargado correctamente`)
    } catch (err: any) {
      toast.error(err?.response?.data?.message || `Error al descargar el ${type.toUpperCase()}`)
    } finally {
      setDownloadingId(null)
      setDownloadType(null)
    }
  }

  const isGlobalInvoice = (rfc: string) => rfc === 'XAXX010101000'

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="flex flex-col gap-0 p-0 sm:max-w-4xl max-h-[90vh]">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
            <DialogTitle className="flex items-center gap-2.5 text-base font-semibold text-inherit">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
                <FileText className="h-4 w-4 text-blue-600" />
              </div>
              Historial de Facturas Emitidas
            </DialogTitle>
            <DialogDescription className="mt-1.5">
              Consulta, descarga y cancela las facturas y notas de crédito generadas desde Facturama.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col flex-1 min-h-0">
            {/* Filters */}
            <div className="p-4 border-b border-slate-100 bg-primary/5/50 flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px] flex items-center bg-card border border-border rounded-md px-3 py-1.5 focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary transition-all">
                <Search className="h-4 w-4 text-slate-400 mr-2 shrink-0" />
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="Buscar por RFC, Razón Social o Folio..."
                  className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
                />
              </div>
              <div className="w-[180px]">
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="bg-card">
                    <SelectValue placeholder="Tipo de CFDI" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="I">Ingreso (Facturas)</SelectItem>
                    <SelectItem value="E">Egreso (Notas de Crédito)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-[160px]">
                <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setPagination(p => ({ ...p, page: 1 })) }}>
                  <SelectTrigger className="bg-card">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Vigentes</SelectItem>
                    <SelectItem value="canceled">Canceladas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isLoading || isRefetching} title="Actualizar">
                <RefreshCw className={cn('h-4 w-4 text-inherit opacity-70', isRefetching && 'animate-spin')} />
              </Button>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                  <Loader2 className="h-8 w-8 text-slate-300 animate-spin" />
                  <p className="text-sm text-inherit opacity-70 font-medium">Cargando facturas...</p>
                </div>
              ) : !invoices || invoices.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                    <FileText className="h-5 w-5 text-slate-400" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-inherit">No se encontraron facturas</p>
                    <p className="text-xs text-inherit opacity-70 mt-1">Intenta ajustando los filtros de búsqueda.</p>
                  </div>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-primary/5 sticky top-0 z-10 shadow-sm shadow-slate-100">
                    <tr>
                      <th className="px-6 py-3 text-left font-medium text-inherit">Folio / Serie</th>
                      <th className="px-6 py-3 text-left font-medium text-inherit">Fecha Emisión</th>
                      <th className="px-6 py-3 text-left font-medium text-inherit">Receptor</th>
                      <th className="px-6 py-3 text-left font-medium text-inherit">Total</th>
                      <th className="px-6 py-3 text-left font-medium text-inherit">Estado</th>
                      <th className="px-6 py-3 text-right font-medium text-inherit">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {paginatedInvoices.map((inv: any) => {
                      const isCanceled = filterStatus === 'canceled'
                      const label = `${inv.Serie ?? ''}${inv.Folio ?? ''}`
                      return (
                        <tr key={inv.Id} className="hover:bg-primary/5/80 transition-colors">
                          <td className="px-6 py-3">
                            <div className="font-medium text-inherit">{label || '—'}</div>
                            <div className="text-xs text-slate-400 font-mono mt-0.5">{inv.Id}</div>
                          </td>
                          <td className="px-6 py-3 text-inherit opacity-70">
                            {new Date(inv.Date).toLocaleString('es-MX', {
                              year: 'numeric', month: 'short', day: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </td>
                          <td className="px-6 py-3">
                            <div className="flex items-center gap-2">
                              {!isGlobalInvoice(inv.Rfc) ? (
                                <Badge variant="outline" className="text-[10px] py-0 h-4 px-1.5 bg-blue-50 text-blue-600 border-blue-200">
                                  {inv.Rfc}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px] py-0 h-4 px-1.5 bg-blue-50 text-blue-600 border-blue-200">
                                  Público en General
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-3 font-medium text-inherit">
                            ${Number(inv.Total).toFixed(2)}
                          </td>
                          <td className="px-6 py-3">
                            {isCanceled ? (
                              <Badge className="bg-destructive/10 text-red-700 text-[10px]">Cancelada</Badge>
                            ) : (
                              <Badge className="bg-green-100 text-green-700 text-[10px]">Vigente</Badge>
                            )}
                          </td>
                          <td className="px-6 py-3 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              {/* PDF */}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-destructive hover:text-red-700 hover:bg-destructive/10"
                                title="Descargar PDF"
                                disabled={downloadingId === inv.Id || isCanceled}
                                onClick={() => handleDownload(inv.Id, 'pdf')}
                              >
                                {downloadingId === inv.Id && downloadType === 'pdf' ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <FileText className="h-4 w-4" />
                                )}
                              </Button>
                              {/* XML */}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                title="Descargar XML"
                                disabled={downloadingId === inv.Id || isCanceled}
                                onClick={() => handleDownload(inv.Id, 'xml')}
                              >
                                {downloadingId === inv.Id && downloadType === 'xml' ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <FileText className="h-4 w-4" />
                                )}
                              </Button>
                              {/* Cancelar CFDI — solo vigentes */}
                              {canCancelSale && !isCanceled && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-slate-400 hover:text-destructive hover:bg-destructive/10"
                                  title="Cancelar CFDI ante el SAT"
                                  onClick={() => setCancelCfdiTarget({ cfdiId: inv.Id, label })}
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Paginator */}
          <GlobalPaginator
            totalItems={totalInvoices}
            itemsPerPage={pagination.pageSize}
            currentPage={pagination.page}
            from={from}
            to={to}
            onPageChange={(page) => setPagination((prev) => ({ ...prev, page }))}
          />

          <div className="p-4 border-t border-slate-100 flex justify-end">
            <Button variant="outline" onClick={onClose}>
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de cancelación CFDI */}
      {canCancelSale && cancelCfdiTarget && (
        <CancelCfdiModal
          isOpen={!!cancelCfdiTarget}
          onClose={() => setCancelCfdiTarget(null)}
          cfdiId={cancelCfdiTarget.cfdiId}
          cfdiLabel={cancelCfdiTarget.label}
          onSuccess={() => {
            refetch()
            setCancelCfdiTarget(null)
          }}
        />
      )}
    </>
  )
}
